import React, { useState } from 'react';
import { WORKFLOW_STAGES, TIMETABLE, PRODUCT_TYPES } from '../../data/constants';
import ProductCard from './ProductCard';
import CalendarView from './CalendarView';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiTarget, FiCalendar, FiColumns, FiCheck } from 'react-icons/fi';

const WorkflowTab = ({ 
  products, 
  onAddProduct, 
  onUpdateProduct, 
  onDeleteProduct, 
  onMoveProduct,
  targets, 
  setTargets, 
  designCounter, 
  shops, 
  activeShopId, 
  productionTasks, 
  researchTasks 
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [filterMode, setFilterMode] = useState('active');
  
  const [newProductForm, setNewProductForm] = useState({
    title: '',
    types: ['T-Shirt'],
    priority: 'Medium',
    notes: '',
    targetDate: new Date().toISOString().split('T')[0],
    shopIds: activeShopId ? [activeShopId] : [],
    checklist: {}
  });

  const addProduct = () => {
    if (!newProductForm.title.trim()) return;
    const formattedCounter = String(designCounter).padStart(3, '0');
    
    const newProduct = {
      ...newProductForm,
      designNumber: `DSN-${formattedCounter}`,
      status: 'Ideas',
      createdAt: new Date().toISOString()
    };

    onAddProduct(newProduct);
    
    setNewProductForm({
      title: '',
      types: ['T-Shirt'],
      priority: 'Medium',
      notes: '',
      targetDate: new Date().toISOString().split('T')[0],
      shopIds: activeShopId ? [activeShopId] : [],
      checklist: {}
    });
    setShowAddForm(false);
  };

  const toggleTypeInForm = (type) => {
    const current = newProductForm.types || [];
    const updated = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    if (updated.length > 0) {
      setNewProductForm({ ...newProductForm, types: updated });
    }
  };

  const toggleShopInAddForm = (shopId) => {
    const current = newProductForm.shopIds || [];
    const updated = current.includes(shopId) ? current.filter(id => id !== shopId) : [...current, shopId];
    setNewProductForm({ ...newProductForm, shopIds: updated });
  };

  const filteredProducts = products.filter(p => filterMode === 'all' || p.shopIds?.includes(activeShopId));
  const designsCompleted = products.filter(p => (p.status === 'Listed' || p.status === 'Live') && (filterMode === 'all' || p.shopIds?.includes(activeShopId))).length;

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 animate-fade-in space-y-12">
      <section>
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sage text-white flex items-center justify-center font-serif font-bold text-lg">A</div>
            <h2 className="text-2xl text-espresso">Design Production Pipeline</h2>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-white p-1 rounded-xl border border-cream-dark shadow-sm">
              <button onClick={() => setFilterMode('active')} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${filterMode === 'active' ? 'bg-terracotta text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}>
                Current Shop
              </button>
              <button onClick={() => setFilterMode('all')} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${filterMode === 'all' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}>
                All Shops
              </button>
            </div>

            <div className="flex bg-white p-1 rounded-xl border border-cream-dark shadow-sm">
              <button onClick={() => setViewMode('kanban')} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === 'kanban' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}>
                <SafeIcon icon={FiColumns} /> Board
              </button>
              <button onClick={() => setViewMode('calendar')} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === 'calendar' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}>
                <SafeIcon icon={FiCalendar} /> Calendar
              </button>
            </div>

            <div className="flex gap-4 bg-white p-2.5 rounded-xl border border-cream-dark shadow-sm">
              <div className="flex items-center gap-2 pr-3 border-r border-cream-dark">
                <SafeIcon icon={FiTarget} className="text-terracotta w-4 h-4" />
                <div className="text-[10px] font-bold text-espresso-light uppercase">W: {designsCompleted}/{targets.weekly}</div>
              </div>
              <div className="text-[10px] font-bold text-espresso-light uppercase">M: {designsCompleted}/{targets.monthly}</div>
            </div>
          </div>
        </div>

        {viewMode === 'kanban' ? (
          <div className="bg-cream-dark/20 p-4 sm:p-6 rounded-2xl border border-cream-dark overflow-x-auto kanban-scroll text-nowrap">
            <div className="flex gap-6 min-w-max pb-4">
              {WORKFLOW_STAGES.map(stage => {
                const stageProducts = filteredProducts.filter(p => p.status === stage);
                const isIdeasStage = stage === 'Ideas';

                return (
                  <div key={stage} className="w-72 flex flex-col">
                    <div className="flex items-center justify-between mb-4 px-2">
                      <h3 className="font-bold text-espresso uppercase tracking-wider text-sm">{stage}</h3>
                      <span className="bg-white px-2 py-0.5 rounded-full text-xs font-bold text-espresso-light shadow-sm border border-cream-dark">
                        {stageProducts.length}
                      </span>
                    </div>

                    <div className="flex-1 bg-cream/50 rounded-xl p-3 min-h-[400px] border border-cream-dark/50 whitespace-normal">
                      {isIdeasStage && showAddForm && (
                        <div className="bg-white p-4 rounded-xl border-2 border-terracotta shadow-md mb-3 animate-fade-in space-y-4">
                          <input 
                            autoFocus 
                            className="w-full text-sm font-bold border-b border-cream-dark pb-1 focus:outline-none focus:border-terracotta bg-transparent"
                            value={newProductForm.title}
                            onChange={e => setNewProductForm({ ...newProductForm, title: e.target.value })}
                            placeholder="Design Idea Title..."
                          />
                          <div>
                            <label className="block text-[9px] font-bold text-espresso-light/60 uppercase mb-2">Product Types</label>
                            <div className="grid grid-cols-2 gap-1.5">
                              {PRODUCT_TYPES.map(t => (
                                <button key={t} type="button" onClick={() => toggleTypeInForm(t)} className={`flex items-center justify-between px-2 py-1.5 rounded-lg border text-[10px] transition-all ${newProductForm.types?.includes(t) ? 'bg-sage/10 border-sage text-sage font-bold' : 'bg-white border-cream-dark text-espresso-light'}`}>
                                  {t}
                                  {newProductForm.types?.includes(t) && <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" />}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => setShowAddForm(false)} className="flex-1 py-2 text-espresso-light text-xs font-medium hover:bg-cream-dark rounded-lg">Cancel</button>
                            <button onClick={addProduct} className="flex-2 py-2 px-4 bg-terracotta text-white text-xs font-bold rounded-lg hover:bg-terracotta/90 transition-colors shadow-sm">Add Idea</button>
                          </div>
                        </div>
                      )}

                      {stageProducts.map(product => (
                        <ProductCard 
                          key={product.id} 
                          product={product} 
                          updateProduct={onUpdateProduct} 
                          deleteProduct={onDeleteProduct} 
                          moveProduct={onMoveProduct} 
                          shops={shops} 
                          productionTasks={productionTasks} 
                          researchTasks={researchTasks} 
                        />
                      ))}

                      {isIdeasStage && !showAddForm && (
                        <button onClick={() => setShowAddForm(true)} className="w-full py-4 flex items-center justify-center gap-2 text-sm font-bold text-espresso-light border-2 border-dashed border-cream-dark rounded-xl hover:border-terracotta hover:text-terracotta transition-colors bg-white/50">
                          <SafeIcon icon={FiPlus} /> New Design Idea
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <CalendarView products={filteredProducts} />
        )}
      </section>

      <section>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-espresso text-white flex items-center justify-center font-serif font-bold text-lg">B</div>
          <h2 className="text-2xl text-espresso">Suggested Weekly Rhythm</h2>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-cream-dark overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-7 divide-y sm:divide-y-0 sm:divide-x divide-cream-dark">
            {TIMETABLE.map((day, i) => (
              <div key={day.day} className={`p-4 ${i === 5 || i === 6 ? 'bg-cream/30' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiCalendar} className="text-terracotta/70 w-4 h-4" />
                  <h4 className="font-bold text-espresso text-sm uppercase tracking-wide">{day.day}</h4>
                </div>
                <p className="text-sm text-espresso-light leading-snug">{day.focus}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkflowTab;